import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/articleTypes';
import { articles } from '@/mocks/articles';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  const handleArticleClick = (articleSlug: string) => {
    navigate(`/article/${articleSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const seoTitle = currentCategory
    ? `${currentCategory.name} - 少妇护肤美容指南`
    : '分类页面 - 少妇护肤美容指南';
  const seoDesc = currentCategory
    ? currentCategory.description
    : '浏览不同分类的少妇护肤美容文章';

  return (
    <>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
      <meta name="keywords" content={`少妇,少妇护肤,${currentCategory?.name || '护肤'},少妇美容`} />
      <link rel="canonical" href={`https://bowenzhilian.cn/category/${slug}`} />

      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              {currentCategory && (
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${currentCategory.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                      {currentCategory.name}
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">
                      {currentCategory.description}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-10 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div ref={listRef}>
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {filteredArticles.map((article) => (
                    <div
                      key={article.slug}
                      onClick={() => handleArticleClick(article.slug)}
                      className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 md:h-52 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={article.title}
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2.5 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.author}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    暂无文章
                  </h3>
                  <p className="text-sm text-foreground-500">
                    该分类下暂时没有文章，请浏览其他分类
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}